import { supabase } from '../supabaseClient';

export type LeaveType = 'annual' | 'sick' | 'unpaid' | 'other';
export type LeaveStatus = 'pending' | 'approved' | 'denied' | 'cancelled';

interface LeaveRequestRecord {
  id: string;
  company_id: string;
  guard_id: string;
  leave_type: LeaveType;
  start_date: string;
  end_date: string;
  reason: string | null;
  status: LeaveStatus;
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  guard?: { full_name?: string | null; avatar_url?: string | null } | null;
  reviewer?: { full_name?: string | null } | null;
}

export interface LeaveRequest {
  id: string;
  company_id: string;
  guard_id: string;
  leave_type: LeaveType;
  start_date: string;
  end_date: string;
  reason: string | null;
  status: LeaveStatus;
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
  guard_name: string;
  guard_avatar_url: string | null;
  reviewer_name: string | null;
}

export interface GuardOption {
  id: string;
  full_name: string;
  avatar_url: string | null;
}

const LEAVE_REQUEST_SELECT = `
  id,
  company_id,
  guard_id,
  leave_type,
  start_date,
  end_date,
  reason,
  status,
  admin_notes,
  reviewed_by,
  reviewed_at,
  created_by,
  created_at,
  updated_at,
  guard:profiles!guard_id(full_name, avatar_url),
  reviewer:profiles!reviewed_by(full_name)
`;

let cachedCompanyId: string | null = null;

async function getCompanyId(): Promise<string> {
  if (cachedCompanyId) return cachedCompanyId;

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: profile, error } = await supabase
    .from('profiles')
    .select('company_id')
    .eq('id', user.id)
    .single();

  if (error || !profile?.company_id) throw new Error('Could not resolve company');
  cachedCompanyId = profile.company_id;
  return profile.company_id;
}

function toLeaveRequest(row: LeaveRequestRecord): LeaveRequest {
  return {
    id: row.id,
    company_id: row.company_id,
    guard_id: row.guard_id,
    leave_type: row.leave_type,
    start_date: row.start_date,
    end_date: row.end_date,
    reason: row.reason ?? null,
    status: row.status,
    admin_notes: row.admin_notes ?? null,
    reviewed_by: row.reviewed_by ?? null,
    reviewed_at: row.reviewed_at ?? null,
    created_at: row.created_at,
    updated_at: row.updated_at,
    guard_name: row.guard?.full_name?.trim() || 'Unknown guard',
    guard_avatar_url: row.guard?.avatar_url ?? null,
    reviewer_name: row.reviewer?.full_name?.trim() ?? null,
  };
}

export const leaveService = {
  async fetchLeaveRequests(statusFilter?: LeaveStatus): Promise<LeaveRequest[]> {
    const companyId = await getCompanyId();

    let query = supabase
      .from('guard_leave_requests')
      .select(LEAVE_REQUEST_SELECT)
      .eq('company_id', companyId)
      .order('created_at', { ascending: false });

    if (statusFilter) {
      query = query.eq('status', statusFilter);
    }

    const { data, error } = await query;
    if (error) throw error;

    return (data ?? []).map((row) => toLeaveRequest(row as LeaveRequestRecord));
  },

  async approveLeaveRequest(requestId: string, adminNotes?: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const updates: Record<string, unknown> = {
      status: 'approved',
      reviewed_by: user.id,
      reviewed_at: new Date().toISOString(),
    };

    if (adminNotes !== undefined) {
      updates.admin_notes = adminNotes;
    }

    const { error } = await supabase
      .from('guard_leave_requests')
      .update(updates)
      .eq('id', requestId);

    if (error) throw error;
  },

  async denyLeaveRequest(requestId: string, adminNotes?: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const updates: Record<string, unknown> = {
      status: 'denied',
      reviewed_by: user.id,
      reviewed_at: new Date().toISOString(),
    };

    if (adminNotes !== undefined) {
      updates.admin_notes = adminNotes;
    }

    const { error } = await supabase
      .from('guard_leave_requests')
      .update(updates)
      .eq('id', requestId);

    if (error) throw error;
  },

  async createLeaveRequest(params: {
    guardId: string;
    leaveType: LeaveType;
    startDate: string;
    endDate: string;
    reason?: string;
    adminNotes?: string;
  }): Promise<void> {
    const companyId = await getCompanyId();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('guard_leave_requests')
      .insert({
        company_id: companyId,
        guard_id: params.guardId,
        leave_type: params.leaveType,
        start_date: params.startDate,
        end_date: params.endDate,
        reason: params.reason || null,
        admin_notes: params.adminNotes || null,
        status: 'approved',
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString(),
        created_by: user.id,
      });

    if (error) throw error;
  },

  async fetchGuards(): Promise<GuardOption[]> {
    const companyId = await getCompanyId();

    const { data, error } = await supabase
      .from('profiles')
      .select('id, full_name, avatar_url')
      .eq('company_id', companyId)
      .eq('is_guard', true)
      .eq('status', 'active')
      .order('full_name', { ascending: true });

    if (error) throw error;

    return (data ?? []).map((row) => ({
      id: row.id,
      full_name: row.full_name?.trim() || 'Unknown',
      avatar_url: row.avatar_url ?? null,
    }));
  },
};
